import React, { createContext, useContext, useState, ReactNode } from 'react'
import Popup from '../../ui/popup'
import Button from '../../ui/button'
import cls from './reportPopup.Module.sass'
import { postDenounce } from '../../../api/ephemeral'
import { postDenounceComment } from '../../../api/comments'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { RootState } from '../../../store/rootReducer'
import { isUserPro } from '../../../utils/isUserPro'
import './../../ui/textArea/textArea.sass'

type ReportContextType = {
    openReport: (
        title: string,
        contentType: string,
        contentId: string,
        isStory?: boolean,
        extraContent?: ReactNode
    ) => void
    closeReport: () => void
}

const ReportContext = createContext<ReportContextType | null>(null)

export const useReport = () => {
    const ctx = useContext(ReportContext)
    if (!ctx) throw new Error('useReport must be used within ReportProvider')
    return ctx
}

interface ReportProviderProps {
    children: ReactNode
}

export function ReportProvider({ children }: ReportProviderProps) {
    const [t] = useTranslation()

    const user = useSelector((state: RootState) => state.member.user)

    const [isOpen, setIsOpen] = useState(false)
    const [title, setTitle] = useState<string>('')
    const [isLoading, setIsLoading] = useState(false)
    const [errorText, setErrorText] = useState('')
    const [contentType, setContentType] = useState('')
    const [contentId, setContentId] = useState<number>()
    const [isStory, setIsStory] = useState(false)
    const [reportText, setReportText] = useState('')
    const [extraContent, setExtraContent] = useState<ReactNode>(null)

    const [isSuccess, setIsSuccess] = useState(false)

    const openReport = (
        title: string,
        type: 'contribution' | 'contribution_comment',
        id: number,
        story = false,
        content?: ReactNode
    ) => {
        setTitle(title)
        setContentType(type)
        setContentId(id)
        setIsStory(story)
        setExtraContent(content || null)
        setIsOpen(true)
        setIsSuccess(false)
    }

    const closeReport = () => {
        setIsOpen(false)
        setIsLoading(false)
        setErrorText('')
        setReportText('')
        setExtraContent(null)
    }

    const handleSendReport = async () => {
        if (!contentId) return
        setIsLoading(true)
        setErrorText('')

        try {
            const response = isStory
                ? await postDenounce({
                      id: contentId,
                      message: reportText
                  })
                : //@ts-ignore
                  await postDenounceComment({ object_id: contentId, object_type: contentType, message: reportText })

            if (response.status === 'success') {
                setTimeout(() => setIsSuccess(true), 150)
                setTimeout(() => {
                    setIsLoading(false)
                    closeReport()
                }, 1250)
            } else {
                setIsLoading(false)
                setErrorText(response.message || 'Something went wrong')
            }
        } catch (err: any) {
            setIsLoading(false)
            if (err && err.message) {
                setErrorText(err.message)
            } else {
                setErrorText('Network error')
            }
        }
    }

    return (
        //@ts-ignore
        <ReportContext.Provider value={{ openReport, closeReport }}>
            {children}

            {isOpen && (
                <Popup title={title} onClose={closeReport} classname={cls['reportPopup']}>
                    <div className={cls['reportContent']}>
                        {extraContent && <div className={cls['reportExtra']}>{extraContent}</div>}

                        <i>{t('Describe the reason for your report:')}</i>
                        <textarea
                            className={cls['reportText']}
                            value={reportText}
                            onChange={(e) => setReportText(e.target.value)}
                            placeholder={t('Describe the issue...')}
                        />
                        {errorText && (
                            <p style={{ color: '#a23330', fontSize: '12px', marginTop: '8px' }}>{errorText}</p>
                        )}
                        <div className={cls['reportActions']}>
                            <Button secondary label="Cancel" disabled={isLoading} onClick={closeReport} />
                            <Button
                                label={isLoading ? 'Reporting...' : 'Report_button'}
                                disabled={isLoading || (user && !isUserPro(user.member_type) && !reportText)}
                                onClick={handleSendReport}
                                isLoading={isLoading}
                            />
                        </div>
                        {isSuccess && <div className={cls['successBlock']} />}
                    </div>
                </Popup>
            )}
        </ReportContext.Provider>
    )
}
